/**
 * Where may the admin be sent after signing in? Only to a same-origin PATH inside this app.
 *
 * `?redirectUrl=` comes from the address bar, so anyone can write it. react-router keeps a path that starts with `//` or `/\`
 * and falls back to a full-page load when the browser refuses to push it, so `/sign-in?redirectUrl=//evil.com` sent her
 * off-site after she signed in on the real page. Browsers also strip tabs and newlines and treat `\` as `/`, and a
 * percent-encoded `%2F` or `%5C` can be decoded later by something downstream, so those are refused too.
 *
 * Accepted: one leading `/` followed by anything except `/` or `\`; no backslash and no control character anywhere, raw or
 * after decoding (up to three rounds, so `%252F` is caught); no `%5C`. Everything else gets the fallback.
 */
const MAX_LENGTH = 2048;
const MAX_DECODES = 3;

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

const looksSafe = (path: string): boolean =>
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.startsWith("/\\") &&
  !path.includes("\\") &&
  !CONTROL.test(path);

export function safeRedirectPath(value: unknown): string | null;
export function safeRedirectPath<F>(value: unknown, fallback: F): string | F;
export function safeRedirectPath(value: unknown, fallback: unknown = null): unknown {
  if (typeof value !== "string" || value.length === 0 || value.length > MAX_LENGTH) return fallback;
  if (!looksSafe(value) || /%5c/i.test(value)) return fallback;

  let current = value;
  for (let i = 0; i < MAX_DECODES; i += 1) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return fallback; // a malformed escape is not a path we wrote
    }
    if (decoded === current) break;
    if (!looksSafe(decoded)) return fallback;
    current = decoded;
  }
  return value;
}
